// Shared questionnaire definitions — used by Onboarding (sequential first
// fill) and by AnswersReview (per-section editing). Keep this in sync with
// the backend `QuestionnaireIn` Pydantic model (server.py).

export const SCALE_LABELS = ["Discordo totalmente", "", "Concordo totalmente"];

export const QUESTIONS = [
  // ===== Personalidade =====
  { key: "o1", section: "Personalidade", title: "Adoro experimentar coisas que nunca fiz.", type: "scale" },
  { key: "o2", section: "Personalidade", title: "Tenho uma imaginação fértil.", type: "scale" },
  { key: "o3", section: "Personalidade", title: "Aprecio arte, música e ideias abstractas.", type: "scale" },
  { key: "c1", section: "Personalidade", title: "Sou organizado e cumpro prazos.", type: "scale" },
  { key: "c2", section: "Personalidade", title: "Termino o que começo.", type: "scale" },
  { key: "c3", section: "Personalidade", title: "Presto atenção aos detalhes.", type: "scale" },
  { key: "e1", section: "Personalidade", title: "Sinto-me energizado em grupos.", type: "scale" },
  { key: "e2", section: "Personalidade", title: "Inicio conversas com facilidade.", type: "scale" },
  { key: "e3", section: "Personalidade", title: "Gosto de ser o centro das atenções.", type: "scale" },
  { key: "a1", section: "Personalidade", title: "Importo-me genuinamente com os outros.", type: "scale" },
  { key: "a2", section: "Personalidade", title: "Confio nas pessoas até prova em contrário.", type: "scale" },
  { key: "a3", section: "Personalidade", title: "Evito conflitos sempre que possível.", type: "scale" },
  { key: "n1", section: "Personalidade", title: "Preocupo-me com frequência.", type: "scale" },
  { key: "n2", section: "Personalidade", title: "As minhas emoções mudam rapidamente.", type: "scale" },
  { key: "n3", section: "Personalidade", title: "O stress afecta-me fortemente.", type: "scale" },

  // ===== Valores =====
  { key: "attractive_values", section: "Valores", title: "Que qualidades mais admiras numa pessoa?",
    subtitle: "Escolhe 3 a 6 que mais valorizas.", type: "multi", min: 3, max: 6,
    options: [
      "honestidade", "ambição", "compaixão", "lealdade", "criatividade",
      "humildade", "humor", "independência", "vulnerabilidade",
      "espiritualidade", "curiosidade", "resiliência",
    ] },
  { key: "repellent_values", section: "Valores", title: "Que comportamentos consideras inaceitáveis?",
    subtitle: "Escolhe 3 a 6 dealbreakers.", type: "multi", min: 3, max: 6,
    options: [
      "desonestidade", "controlo", "egoísmo", "infidelidade", "vícios",
      "falta de ambição", "drama excessivo", "materialismo",
      "vaidade", "intolerância", "passividade", "agressividade",
    ] },

  // ===== Crenças & estilo de vida =====
  { key: "political_view", section: "Crenças & estilo de vida", title: "Como descreves as tuas convicções políticas?", type: "choice",
    options: [{v:"conservador",l:"Conservador"},{v:"moderado",l:"Moderado"},{v:"progressista",l:"Progressista"}] },
  { key: "religion_importance", section: "Crenças & estilo de vida", title: "Importância da religião na tua vida.", type: "scale" },
  { key: "wants_children", section: "Crenças & estilo de vida", title: "Queres ter (mais) filhos?", type: "choice",
    options: [{v:"sim",l:"Sim"},{v:"talvez",l:"Talvez"},{v:"nao",l:"Não"}] },
  { key: "smoking", section: "Crenças & estilo de vida", title: "Costumas fumar?", type: "choice",
    options: [{v:"nao",l:"Não fumo"},{v:"ocasional",l:"Ocasionalmente"},{v:"sim",l:"Sim"}] },
  { key: "drinking", section: "Crenças & estilo de vida", title: "Como descreves a tua relação com o álcool?", type: "choice",
    options: [{v:"nao",l:"Não bebo"},{v:"raro",l:"Raramente"},{v:"social",l:"Socialmente"},{v:"frequente",l:"Frequentemente"}] },

  // ===== Preferências físicas =====
  { key: "_height_pair", section: "Preferências físicas", title: "Que altura preferes no teu par? (cm)",
    type: "range_pair", minKey: "pref_height_min", maxKey: "pref_height_max",
    min: 140, max: 210, defMin: 160, defMax: 190 },
  { key: "pref_body_styles", section: "Preferências físicas", title: "Que estilos corporais te atraem?",
    subtitle: "Múltipla escolha.", type: "multi", min: 1, max: 5,
    options: ["atlético", "curvilíneo", "magro", "forte", "médio"] },
  { key: "appearance_importance", section: "Preferências físicas", title: "Quão importante é a aparência física para ti?", type: "scale",
    labels: ["Pouco importante", "", "Muito importante"] },
  { key: "pref_dress_styles", section: "Preferências físicas", title: "Que estilos de vestir te atraem mais?",
    type: "multi", min: 1, max: 5,
    options: ["clássico", "casual", "elegante", "desportivo", "alternativo"] },

  // ===== Preferências mentais =====
  { key: "education_importance", section: "Preferências mentais", title: "Importância do nível educacional do teu par.", type: "scale" },
  { key: "ambition_importance", section: "Preferências mentais", title: "Importância da ambição no teu par.", type: "scale" },
  { key: "intellect_importance", section: "Preferências mentais", title: "Importância da curiosidade intelectual.", type: "scale" },
  { key: "humor_style", section: "Preferências mentais", title: "Que estilo de humor mais aprecias?", type: "choice",
    options: [
      {v:"sarcastico",l:"Sarcástico"},
      {v:"leve",l:"Leve e descontraído"},
      {v:"inteligente",l:"Inteligente"},
      {v:"auto-ironico",l:"Auto-irónico"},
      {v:"acido",l:"Ácido"},
    ] },

  // ===== Vida social & espiritual =====
  { key: "social_circle_size", section: "Vida social & espiritual", title: "Que tipo de círculo social prefere o/a teu/tua par?", type: "choice",
    options: [
      {v:"intimo",l:"Pequeno e íntimo"},
      {v:"medio",l:"Equilibrado"},
      {v:"vibrante",l:"Grande e vibrante"},
    ] },
  { key: "going_out_frequency", section: "Vida social & espiritual", title: "Frequência ideal de saídas/eventos sociais.", type: "scale",
    labels: ["Raramente", "", "Muito frequente"] },
  { key: "family_closeness_importance", section: "Vida social & espiritual", title: "Importância de proximidade familiar para o par.", type: "scale" },
  { key: "friends_importance", section: "Vida social & espiritual", title: "Importância dos amigos na vida do par.", type: "scale" },
  { key: "shared_practice_importance", section: "Vida social & espiritual", title: "Importância de partilhar prática espiritual/religiosa.", type: "scale" },
  { key: "mindfulness_importance", section: "Vida social & espiritual", title: "Importância de meditação ou mindfulness.", type: "scale" },
  { key: "belief_in_higher_power", section: "Vida social & espiritual", title: "Como descreves a tua crença em algo maior?", type: "choice",
    options: [
      {v:"forte",l:"Crença forte"},
      {v:"moderada",l:"Moderada"},
      {v:"curiosa",l:"Curiosa / aberta"},
      {v:"cetica",l:"Céptica"},
      {v:"ateia",l:"Ateia"},
    ] },

  // ===== Sonhos, metas e visão =====
  { key: "big_dream", section: "Sonhos & visão", title: "Em uma frase, qual é o teu grande sonho?", type: "text", maxLength: 200,
    placeholder: "Ex: Construir uma quinta sustentável e criar família lá." },
  { key: "vision_5y", section: "Sonhos & visão", title: "Como te imaginas daqui a 5 anos?", type: "choice",
    options: [
      {v:"estabilidade",l:"Estabilidade familiar"},
      {v:"aventura",l:"Aventura pelo mundo"},
      {v:"impacto",l:"Impacto na comunidade"},
      {v:"carreira",l:"Crescimento profissional"},
      {v:"equilibrio",l:"Equilíbrio sereno"},
    ] },
  { key: "ideal_life_keywords", section: "Sonhos & visão", title: "Três palavras que descrevem a tua vida ideal.",
    type: "keywords", min: 3 },
  { key: "ambition_level", section: "Sonhos & visão", title: "Como descreves o teu nível de ambição?", type: "scale",
    labels: ["Discreta", "", "Muito alta"] },
  { key: "legacy_focus", section: "Sonhos & visão", title: "Que legado queres deixar?", type: "choice",
    options: [
      {v:"familia",l:"Família forte e unida"},
      {v:"criativo",l:"Trabalho criativo / artístico"},
      {v:"comunidade",l:"Impacto comunitário"},
      {v:"carreira",l:"Carreira de excelência"},
      {v:"conhecimento",l:"Conhecimento / ideias"},
    ] },

  // ===== Logística =====
  { key: "preferred_age_min", section: "Logística", title: "Idade mínima do par ideal.", type: "number",
    min: 18, max: 80, default: 25 },
  { key: "preferred_age_max", section: "Logística", title: "Idade máxima do par ideal.", type: "number",
    min: 18, max: 99, default: 45 },
  { key: "preferred_distance_km", section: "Logística", title: "Distância máxima (km).", type: "number",
    min: 5, max: 500, default: 50 },
  { key: "communication_style", section: "Logística", title: "O teu estilo de comunicação é...", type: "choice",
    options: [
      {v:"directo",l:"Directo"},
      {v:"ponderado",l:"Ponderado"},
      {v:"afectivo",l:"Afectivo"},
    ] },
];

export const DEFAULT_ANSWERS = {
  o1: 3, o2: 3, o3: 3, c1: 3, c2: 3, c3: 3, e1: 3, e2: 3, e3: 3,
  a1: 3, a2: 3, a3: 3, n1: 3, n2: 3, n3: 3,
  attractive_values: [], repellent_values: [],
  political_view: "moderado", religion_importance: 3, wants_children: "talvez",
  smoking: "nao", drinking: "social",
  pref_height_min: 160, pref_height_max: 190,
  pref_body_styles: [], appearance_importance: 3, pref_dress_styles: [],
  education_importance: 3, ambition_importance: 3, intellect_importance: 3,
  humor_style: "leve",
  social_circle_size: "medio", going_out_frequency: 3,
  family_closeness_importance: 3, friends_importance: 3,
  shared_practice_importance: 3, mindfulness_importance: 3,
  belief_in_higher_power: "moderada",
  big_dream: "", vision_5y: "estabilidade", ideal_life_keywords: [],
  ambition_level: 3, legacy_focus: "familia",
  preferred_age_min: 25, preferred_age_max: 45, preferred_distance_km: 50,
  communication_style: "ponderado",
};

// Group questions by section in display order.
export const SECTIONS = (() => {
  const order = [];
  const map = new Map();
  for (const q of QUESTIONS) {
    if (!map.has(q.section)) {
      map.set(q.section, []);
      order.push(q.section);
    }
    map.get(q.section).push(q);
  }
  return order.map(s => ({ name: s, questions: map.get(s) }));
})();

// Friendly value formatter for the answers review page.
export const formatAnswer = (q, value, allAnswers) => {
  if (value === undefined || value === null || value === "") return "—";
  if (q.type === "scale" || q.type === "number") return String(value);
  if (q.type === "choice") {
    const opt = (q.options || []).find(o => o.v === value);
    return opt ? opt.l : String(value);
  }
  if (q.type === "multi" || q.type === "keywords") {
    if (!Array.isArray(value) || value.length === 0) return "—";
    return value.join(", ");
  }
  if (q.type === "text") return value || "—";
  if (q.type === "range_pair") {
    const lo = allAnswers?.[q.minKey];
    const hi = allAnswers?.[q.maxKey];
    return (lo != null && hi != null) ? `${lo} – ${hi} cm` : "—";
  }
  return String(value);
};
